import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Input, Modal, Form, message, Button, Breadcrumb, Grid, Card, Row, Col,
  Statistic, Tag, Tooltip, Switch, Space, Badge, InputNumber
} from 'antd';
import { vehicleServiceClassesService } from '../../common/services';
import {
  EditOutlined, DeleteOutlined, RightOutlined, SearchOutlined,
  CarOutlined, PlusOutlined, CheckOutlined,
  CloseOutlined, DatabaseOutlined, AppstoreOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

const VehicleServiceClasses = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, used: 0 });

  const [isModalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form] = Form.useForm();

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'small' : 'middle';

  const calculateStats = useCallback((data) => {
    const active = data.filter(x => x.isActive !== false).length;
    const used = data.filter(x => (x.usageCount || 0) > 0).length;
    setStats({
      total: data.length,
      active,
      inactive: data.length - active,
      used,
    });
  }, []);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await vehicleServiceClassesService.getAll();
      const arr = Array.isArray(data) ? data : [];
      setItems(arr);
      calculateStats(arr);
    } catch (err) {
      console.error(err);
      message.error('Erreur lors du chargement des classes de service');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter((it) => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    return (
      it.code?.toLowerCase().includes(term) ||
      it.label?.toLowerCase().includes(term) ||
      it.description?.toLowerCase().includes(term)
    );
  });

  const handleAdd = () => {
    setSelected(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      displayOrder: items.length + 1,
      icon: 'local_taxi_rounded',
      color: '#FA8C16',
    });
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setSelected(record);
    form.setFieldsValue({
      code: record.code,
      label: record.label,
      description: record.description || '',
      icon: record.icon || 'local_taxi_rounded',
      color: record.color || '#FA8C16',
      displayOrder: record.displayOrder || 0,
      isActive: record.isActive !== false,
    });
    setModalOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Supprimer la classe de service',
      content: (
        <div>
          <p>Voulez-vous vraiment supprimer la classe <strong>{record.label}</strong> (<code>{record.code}</code>) ?</p>
          {record.usageCount > 0 && (
            <Tag color="red">
              Bloqué : {record.usageCount} véhicule(s) utilisent cette classe.
            </Tag>
          )}
        </div>
      ),
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          const response = await vehicleServiceClassesService.remove(record._id);
          if (response?.success) {
            message.success('Classe supprimée');
            fetchItems();
          } else {
            message.error(response?.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          message.error(error?.message || 'Erreur lors de la suppression');
        }
      },
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (selected) {
        const response = await vehicleServiceClassesService.update(selected._id, values);
        if (response?.success) {
          message.success('Classe mise à jour');
          setModalOpen(false);
          fetchItems();
        } else {
          message.error(response?.message || 'Erreur');
        }
      } else {
        const response = await vehicleServiceClassesService.create(values);
        if (response?.success) {
          message.success('Classe créée');
          setModalOpen(false);
          fetchItems();
        } else {
          message.error(response?.message || 'Erreur');
        }
      }
    } catch (error) {
      message.error(error?.message || 'Erreur');
    }
  };

  const handleToggle = async (record, checked) => {
    try {
      const response = await vehicleServiceClassesService.toggle(record._id, checked);
      if (response?.success) {
        message.success(checked ? 'Classe activée' : 'Classe désactivée');
        fetchItems();
      } else {
        message.error(response?.message || 'Erreur');
      }
    } catch (error) {
      message.error(error?.message || 'Erreur');
    }
  };

  const handleSeed = () => {
    Modal.confirm({
      title: 'Initialiser les classes de service',
      content: (
        <div>
          <p>Cette action va créer les <strong>19 classes Yango</strong> par défaut.</p>
          <p style={{ color: '#666' }}>
            6 classes seront actives (Standard, Économique, Confort, Confort+, Business, VIP).<br />
            13 autres seront créées en inactif.<br />
            Les classes existantes seront ignorées.
          </p>
        </div>
      ),
      okText: 'Initialiser',
      cancelText: 'Annuler',
      onOk: async () => {
        const hide = message.loading('Initialisation...', 0);
        try {
          const response = await vehicleServiceClassesService.seed();
          hide();
          if (response?.success) {
            message.success(response.message);
            fetchItems();
          } else {
            message.error(response?.message || 'Erreur');
          }
        } catch (error) {
          hide();
          message.error(error?.message || 'Erreur');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Ordre',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      width: 80,
      align: 'center',
      sorter: (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0),
      render: (v) => <Tag color="default">{v ?? 0}</Tag>,
    },
    {
      title: 'Classe',
      key: 'label',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: record.color || '#FA8C16',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 'bold',
            }}
          >
            <CarOutlined style={{ fontSize: 20 }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{record.label}</div>
            <code style={{ fontSize: 12, color: '#888' }}>{record.code}</code>
          </div>
        </div>
      ),
      sorter: (a, b) => (a.label || '').localeCompare(b.label || ''),
    },
    {
      title: 'Utilisation',
      dataIndex: 'usageCount',
      key: 'usageCount',
      width: 130,
      align: 'center',
      render: (count) => (
        <Badge
          count={`${count || 0} véhicule(s)`}
          style={{ backgroundColor: count > 0 ? '#52c41a' : '#d9d9d9' }}
        />
      ),
      sorter: (a, b) => (a.usageCount || 0) - (b.usageCount || 0),
    },
    {
      title: 'Statut',
      key: 'status',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Tooltip title={record.isActive !== false ? 'Active' : 'Inactive'}>
          <Switch
            checked={record.isActive !== false}
            onChange={(checked) => handleToggle(record, checked)}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            size="small"
          />
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      align: 'center',
      render: (_, record) => (
        <Space>
          <Tooltip title="Modifier">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
              style={{ borderRadius: 6, color: '#faad14', borderColor: '#faad14' }}
            />
          </Tooltip>
          <Tooltip title={record.usageCount > 0 ? 'Utilisée par des véhicules' : 'Supprimer'}>
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record)}
              disabled={record.usageCount > 0}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="content-wrapper">
      <style>
        {`
          .stat-card-orange {
            background: linear-gradient(135deg, #fa8c16 0%, #ffc069 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-orange .ant-statistic-title,
          .stat-card-orange .ant-statistic-content {
            color: white !important;
          }
        `}
      </style>

      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
        <Breadcrumb
          separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
          style={{ marginBottom: 12 }}
        >
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#fa8c16' }}>Accueil</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#595959' }}>Classes de service</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={6}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><AppstoreOutlined style={{ marginRight: 6 }} />Total</span>}
                value={stats.total}
                valueStyle={{ fontSize: isMobile ? 18 : 26, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={6}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CheckOutlined style={{ marginRight: 6 }} />Actives</span>}
                value={stats.active}
                valueStyle={{ fontSize: isMobile ? 18 : 26, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={6}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CloseOutlined style={{ marginRight: 6 }} />Inactives</span>}
                value={stats.inactive}
                valueStyle={{ fontSize: isMobile ? 18 : 26, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={6}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CarOutlined style={{ marginRight: 6 }} />Utilisées</span>}
                value={stats.used}
                valueStyle={{ fontSize: isMobile ? 18 : 26, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>

        <Card style={{ borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={12}>
              <Input
                prefix={<SearchOutlined style={{ color: '#fa8c16' }} />}
                placeholder="Rechercher (code, libellé, description)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
                size={controlSize}
                style={{ borderRadius: 8 }}
              />
            </Col>
            <Col xs={24} sm={12} style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <Button
                icon={<DatabaseOutlined />}
                onClick={handleSeed}
                size={controlSize}
                type="dashed"
              >
                Initialiser
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                size={controlSize}
                style={{
                  background: 'linear-gradient(135deg, #fa8c16 0%, #ffc069 100%)',
                  border: 'none',
                }}
              >
                Ajouter une classe
              </Button>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={filteredItems.map((it) => ({ ...it, key: it._id }))}
            pagination={{ pageSize: 15, showSizeChanger: true }}
            loading={isLoading}
            size={controlSize}
            scroll={{ x: 700 }}
          />
        </Card>
      </div>

      <Modal
        title={selected ? 'Modifier la classe de service' : 'Ajouter une classe de service'}
        open={isModalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText={selected ? 'Modifier' : 'Ajouter'}
        cancelText="Annuler"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Code (Yango)"
                name="code"
                rules={[{ required: true, message: 'Le code est requis' }]}
                tooltip="Doit correspondre exactement à la valeur Yango Fleet (ex: standart, econom, comfort_plus). Lowercase, sans espace."
              >
                <Input placeholder="ex: standart, econom, comfort_plus" disabled={!!selected} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Libellé (FR)"
                name="label"
                rules={[{ required: true, message: 'Le libellé est requis' }]}
              >
                <Input placeholder="ex: Standard, Économique" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} placeholder="Description optionnelle" />
          </Form.Item>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Icône (Material Icon)"
                name="icon"
                tooltip="Nom de l'icône Material : local_taxi_rounded, attach_money_rounded, star_rounded, diamond_rounded..."
              >
                <Input placeholder="ex: local_taxi_rounded" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Couleur"
                name="color"
                tooltip="Hex (ex: #2196F3)"
              >
                <Input type="color" style={{ width: '100%', height: 32, padding: 2 }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="Ordre" name="displayOrder">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VehicleServiceClasses;
